/**
 * Asset Compression Pipeline
 *
 * Handles client-side compression of images and SVGs before upload so that
 * project/template JSON stays small. Returns the compressed blob, a thumbnail
 * blob, and metadata describing the savings.
 */

export interface CompressionProfile {
  maxDimension: number;
  quality: number;
  preferWebp: boolean;
  inlineSvgThresholdBytes: number;
  thumbnailSize: number;
}

export const DEFAULT_COMPRESSION_PROFILE: CompressionProfile = {
  maxDimension: 2400,
  quality: 0.82,
  preferWebp: true,
  inlineSvgThresholdBytes: 32 * 1024,
  thumbnailSize: 256,
};

export const QUALITY_PRESETS: Record<string, CompressionProfile> = {
  high: {
    maxDimension: 3600,
    quality: 0.92,
    preferWebp: true,
    inlineSvgThresholdBytes: 32 * 1024,
    thumbnailSize: 384,
  },
  standard: DEFAULT_COMPRESSION_PROFILE,
  aggressive: {
    maxDimension: 1600,
    quality: 0.7,
    preferWebp: true,
    inlineSvgThresholdBytes: 24 * 1024,
    thumbnailSize: 200,
  },
};

const PROFILE_KEY = 'cs:assetCompressionProfile';

export function loadCompressionProfile(): CompressionProfile {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return DEFAULT_COMPRESSION_PROFILE;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_COMPRESSION_PROFILE, ...parsed };
  } catch {
    return DEFAULT_COMPRESSION_PROFILE;
  }
}

export function saveCompressionProfile(profile: CompressionProfile): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch {
    // storage may be full or unavailable; non-critical
  }
}

export interface CompressionResult {
  type: 'image' | 'svg';
  mainBlob: Blob;
  mainExt: string;
  mainMime: string;
  thumbnailBlob: Blob;
  width: number;
  height: number;
  originalSize: number;
  compressedSize: number;
  inlineSvgMarkup?: string;
  compressed: boolean;
}

export async function compressAsset(
  file: File,
  profile: CompressionProfile = loadCompressionProfile()
): Promise<CompressionResult> {
  const isSvg = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
  if (isSvg) {
    return compressSvg(file, profile);
  }
  return compressRaster(file, profile);
}

async function compressRaster(
  file: File,
  profile: CompressionProfile
): Promise<CompressionResult> {
  const bitmap = await loadBitmap(file);
  const { width: origW, height: origH } = bitmap;

  const longest = Math.max(origW, origH);
  const scale = longest > profile.maxDimension ? profile.maxDimension / longest : 1;
  const targetW = Math.max(1, Math.round(origW * scale));
  const targetH = Math.max(1, Math.round(origH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  const mime = profile.preferWebp ? 'image/webp' : 'image/jpeg';
  const ext = profile.preferWebp ? 'webp' : 'jpg';
  let mainBlob = await canvasToBlob(canvas, mime, profile.quality);

  let compressed = true;
  if (mainBlob.size >= file.size && scale === 1) {
    mainBlob = file;
    compressed = false;
  }

  const thumbCanvas = document.createElement('canvas');
  const thumbLong = Math.max(targetW, targetH);
  const thumbScale = thumbLong > profile.thumbnailSize ? profile.thumbnailSize / thumbLong : 1;
  thumbCanvas.width = Math.max(1, Math.round(targetW * thumbScale));
  thumbCanvas.height = Math.max(1, Math.round(targetH * thumbScale));
  const thumbCtx = thumbCanvas.getContext('2d');
  if (!thumbCtx) throw new Error('Thumbnail canvas context unavailable');
  thumbCtx.drawImage(bitmap, 0, 0, thumbCanvas.width, thumbCanvas.height);
  const thumbnailBlob = await canvasToBlob(thumbCanvas, 'image/webp', 0.78);

  if ('close' in bitmap && typeof (bitmap as ImageBitmap).close === 'function') {
    (bitmap as ImageBitmap).close();
  }

  return {
    type: 'image',
    mainBlob,
    mainExt: compressed ? ext : guessExtension(file),
    mainMime: compressed ? mime : file.type || 'application/octet-stream',
    thumbnailBlob,
    width: targetW,
    height: targetH,
    originalSize: file.size,
    compressedSize: mainBlob.size,
    compressed,
  };
}

async function compressSvg(
  file: File,
  profile: CompressionProfile
): Promise<CompressionResult> {
  const raw = await file.text();
  const cleaned = sanitizeAndMinifySvg(raw);
  const { width, height } = extractSvgDimensions(cleaned);

  const mainBlob = new Blob([cleaned], { type: 'image/svg+xml' });
  const thumbnailBlob = await rasterizeSvgToThumbnail(cleaned, profile.thumbnailSize);

  const inlineSvgMarkup = mainBlob.size <= profile.inlineSvgThresholdBytes ? cleaned : undefined;

  return {
    type: 'svg',
    mainBlob,
    mainExt: 'svg',
    mainMime: 'image/svg+xml',
    thumbnailBlob,
    width,
    height,
    originalSize: file.size,
    compressedSize: mainBlob.size,
    inlineSvgMarkup,
    compressed: mainBlob.size < file.size,
  };
}

function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Canvas toBlob returned null'));
        else resolve(blob);
      },
      mime,
      quality
    );
  });
}

function guessExtension(file: File): string {
  const m = /\.([a-z0-9]+)$/i.exec(file.name);
  if (m) return m[1].toLowerCase();
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/jpeg') return 'jpg';
  if (file.type === 'image/webp') return 'webp';
  if (file.type === 'image/gif') return 'gif';
  return 'bin';
}

function sanitizeAndMinifySvg(raw: string): string {
  let out = raw;
  out = out.replace(/<!--[\s\S]*?-->/g, '');
  out = out.replace(/<\?xml[^>]*\?>\s*/g, '');
  out = out.replace(/<!DOCTYPE[^>]*>\s*/gi, '');
  out = out.replace(/<script[\s\S]*?<\/script>/gi, '');
  out = out.replace(/<metadata[\s\S]*?<\/metadata>/gi, '');
  out = out.replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '');
  out = out.replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '');
  out = out.replace(/javascript:/gi, '');
  out = out.replace(/>\s+</g, '><');
  out = out.replace(/\s{2,}/g, ' ');
  out = out.replace(/(\d+\.\d{4,})/g, (_m, n) => Number(n).toFixed(3));
  return out.trim();
}

function extractSvgDimensions(svg: string): { width: number; height: number } {
  const viewBoxMatch = /viewBox\s*=\s*"([^"]+)"/i.exec(svg);
  if (viewBoxMatch) {
    const parts = viewBoxMatch[1].trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((n) => !Number.isNaN(n))) {
      return { width: Math.round(parts[2]), height: Math.round(parts[3]) };
    }
  }
  const wMatch = /\swidth\s*=\s*"([\d.]+)/i.exec(svg);
  const hMatch = /\sheight\s*=\s*"([\d.]+)/i.exec(svg);
  const width = wMatch ? Math.round(parseFloat(wMatch[1])) : 256;
  const height = hMatch ? Math.round(parseFloat(hMatch[1])) : 256;
  return { width, height };
}

async function rasterizeSvgToThumbnail(svgMarkup: string, targetSize: number): Promise<Blob> {
  const blob = new Blob([svgMarkup], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadHtmlImage(url);
    const iw = img.naturalWidth || targetSize;
    const ih = img.naturalHeight || targetSize;
    const longest = Math.max(iw, ih);
    const scale = longest > targetSize ? targetSize / longest : 1;
    const w = Math.max(1, Math.round(iw * scale));
    const h = Math.max(1, Math.round(ih * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Thumbnail context unavailable');
    ctx.drawImage(img, 0, 0, w, h);
    return await canvasToBlob(canvas, 'image/webp', 0.82);
  } catch {
    return new Blob([svgMarkup], { type: 'image/svg+xml' });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function percentSaved(original: number, compressed: number): number {
  if (!original || compressed >= original) return 0;
  return Math.round((1 - compressed / original) * 100);
}
